import type { VendorOption } from './home_value_checks';

/** A vendor answer without a number (BOT-505). */
export type StockStatus = 'in_stock' | 'out_of_stock' | 'direct_ship' | 'no_pdc_section';
/** All items' server-side sort columns (BOT-505). */
export type ProductSortKey = 'vendor_stock' | 'dealer_stock' | 'on_hand';
export type SortDir = 'asc' | 'desc';
export interface ProductSort { key: ProductSortKey; dir: SortDir }

export interface Product {
  ns_product_id: number;
  sku: string | null;
  name: string | null;
  manufacturer: string | null;
  vendor: string | null;
  purchase_price: number | null;
  /** Core charge amount from NetSuite — null when the item has none (BOT-369). */
  core_charge: number | null;
  website_price: number | null;
  on_hand: number;
  item_status: string | null;
  website_visible: string | null;
  inactive: boolean;
  magento_id: number | null;
  // Vendor stock (BOT-505) — null is "no readable number", never zero.
  /** true = stock at the vendor's PDCs, false = the vendor said none, null = unknown. */
  stock_available: boolean | null;
  /** Units across the vendor's PDCs. */
  vendor_stock: number | null;
  dealer_stock: number | null;
  stock_status: StockStatus | null;
  /** The item's vendor has a stock source (lane) at all. */
  stock_has_source: boolean;
  stock_observed_at: string | null;
  /** "YYYY-MM-DD", ET calendar day of the reading. */
  stock_observed_day: string | null;
  stock_days_ago: number | null;
  /** Read more than 30 days ago — shown muted. */
  stock_stale: boolean;
}

export interface ProductsPage {
  items: Product[];
  total: number;
  page: number;
  per_page: number;
}

export interface ProductFilters {
  search?: string;
  vendor?: string[];
  manufacturer?: string;
  purchase_price_min?: number;
  purchase_price_max?: number;
  on_hand_min?: number;
  status?: 'active' | 'inactive' | 'all';
  vendor_stock?: 'in_stock' | 'zero' | 'no_data';
  vendor_stock_min?: number;
  sort?: ProductSortKey;
  dir?: SortDir;
  page?: number;
  per_page?: number;
}

/** Excel-style header cycle: a new column sorts largest first, the same column
 *  flips to smallest first, a third click turns the sort off. */
export function nextSort(current: ProductSort | null, key: ProductSortKey): ProductSort | null {
  if (!current || current.key !== key) return { key, dir: 'desc' };
  return current.dir === 'desc' ? { key, dir: 'asc' } : null;
}

export async function fetchProducts(filters: ProductFilters = {}): Promise<ProductsPage> {
  const params = new URLSearchParams();
  if (filters.search)                          params.set('search', filters.search);
  for (const v of filters.vendor ?? [])        params.append('vendor', v);
  if (filters.manufacturer)                    params.set('manufacturer', filters.manufacturer);
  if (filters.purchase_price_min !== undefined) params.set('purchase_price_min', String(filters.purchase_price_min));
  if (filters.purchase_price_max !== undefined) params.set('purchase_price_max', String(filters.purchase_price_max));
  if (filters.on_hand_min !== undefined)        params.set('on_hand_min', String(filters.on_hand_min));
  if (filters.status)                          params.set('status', filters.status);
  if (filters.vendor_stock)                    params.set('vendor_stock', filters.vendor_stock);
  if (filters.vendor_stock_min !== undefined)  params.set('vendor_stock_min', String(filters.vendor_stock_min));
  if (filters.sort)                            params.set('sort', filters.sort);
  if (filters.sort && filters.dir)             params.set('dir', filters.dir);
  if (filters.page)                            params.set('page', String(filters.page));
  if (filters.per_page)                        params.set('per_page', String(filters.per_page));

  const res = await fetch(`/api/v1/products?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Products fetch failed: ${res.status}`);
  return res.json() as Promise<ProductsPage>;
}

/** The All items vendor dropdown: every NS vendor with its client name. */
export async function fetchProductVendors(): Promise<VendorOption[]> {
  const res = await fetch('/api/v1/products/vendor-codes', { credentials: 'include' });
  if (!res.ok) throw new Error(`Vendors fetch failed: ${res.status}`);
  const data = await res.json() as { vendors: VendorOption[] };
  return data.vendors;
}

import type { Check } from './checks';

export type ProductDetail = Record<string, unknown> & {
  ns_product_id: number;
  sku: string | null;
  name: string | null;
  /** Latest greenlight check for this item (null — never checked). */
  price_check?: Check | null;
  /** Check history, newest first (capped at 50). */
  history?: Check[];
};

/** Read one dynamic tbnsdata column as display text.
 *  `ProductDetail` is an open record (the endpoint does `SELECT *`), so every
 *  column outside the declared fields arrives as `unknown`. */
export function detailText(detail: ProductDetail | null | undefined, field: string): string | null {
  const v = detail?.[field];
  if (v == null || v === '') return null;
  return String(v);
}

/** Read one dynamic tbnsdata column as a number, for the money formatters.
 *  Returns null — never NaN — when the column is absent, null, or not numeric,
 *  so callers can hand the result straight to `fmtPrice` and get "—". */
export function detailNumber(detail: ProductDetail | null | undefined, field: string): number | null {
  const v = detail?.[field];
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export async function fetchProductDetail(nsProductId: number): Promise<ProductDetail> {
  const res = await fetch(`/api/v1/products/${nsProductId}`, { credentials: 'include' });
  if (res.status === 404) throw new Error('Product not found');
  if (!res.ok) throw new Error(`Product fetch failed: ${res.status}`);
  return res.json() as Promise<ProductDetail>;
}

// ── Item freshness (BOT-484) ────────────────────────────────────────────────

export type FreshnessBand = 'green' | 'amber' | 'red' | 'gray';
export type AlertColor = 'amber' | 'red' | 'gray';

export interface FreshnessValue {
  /** confirmed — a data source read it; never — it has a lane but no read yet;
   *  no_source — the item's vendor has no lane for this value. */
  state: 'confirmed' | 'never' | 'no_source';
  confirmed_at: string | null;
  /** Calendar day "YYYY-MM-DD" the API chose for display (ET, CDK by file date). */
  confirmed_day: string | null;
  /** Whole 24-hour periods — drives the band (server side), same rule as the dashboard. */
  age_days: number | null;
  /** Calendar days in ET since confirmed_day — what the UI prints ("7 days ago"). */
  days_ago: number | null;
  band: FreshnessBand;
  /** A check for this value differs from NetSuite and waits in Review. */
  pending_review: boolean;
}

export interface FreshnessAlertPart {
  value: 'price' | 'home_value';
  reason: 'old' | 'never' | 'in_review';
  days_ago: number | null;
  pending_review: boolean;
  color: AlertColor;
}

export interface ItemFreshness {
  as_of: string;
  in_scope: boolean;
  price: FreshnessValue;
  home_value: FreshnessValue;
  alert: { color: AlertColor; parts: FreshnessAlertPart[] } | null;
}

export async function fetchItemFreshness(nsProductId: number): Promise<ItemFreshness> {
  const res = await fetch(`/api/v1/products/${nsProductId}/freshness`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Freshness fetch failed: ${res.status}`);
  return res.json() as Promise<ItemFreshness>;
}
